import { isSafeHostname } from '@/lib/apUtils'
import { PLATFORMS } from '@/lib/linkedAccountIcons'
import {
    assertSafeMastodonUrl,
    canonicalizeInstanceOrigin,
    mastodonFetchJsonWithStatus,
} from '@/lib/mastodonFetch'
import { isSafeUrl } from '@/lib/url'
import type { LinkedAccount } from '@/pages/Profile/Profile.types'

const LEGACY_SCOPES = 'read write follow'
const FALLBACK_SCOPES = LEGACY_SCOPES
const SCOPES = `${LEGACY_SCOPES} push`
const APP_NAME = 'Mackinac Web'
const APP_WEBSITE = 'https://mackinac.social'
const FETCH_TIMEOUT_MS = 10_000
// How long a `push`-rejection is trusted before registerApp tries requesting
// `push` again. Without a TTL, a server that rejects `push` once would be
// re-registered on the fallback scopes forever.
const PUSH_REJECTION_TTL_MS = 14 * 24 * 60 * 60 * 1000

interface MastodonApp {
    client_id: string
    vapid_key: string
    instance_url: string
    client_secret?: string // held in memory only; never persisted to localStorage
    scopes?: string
    pushRejectedAt?: number
}

function sameScopeSet(left: string, right: string): boolean {
    const leftSet = new Set(left.split(/\s+/).filter(Boolean))
    const rightSet = new Set(right.split(/\s+/).filter(Boolean))
    return leftSet.size === rightSet.size && [...leftSet].every(scope => rightSet.has(scope))
}

/**
 * Parse the `scopes` field a registration response may carry, as either a
 * whitespace-delimited string or an array of strings. Returns `undefined`
 * (not a signal either way) when the field is absent, empty, or neither
 * shape — callers then keep trusting the scopes they requested.
 */
function parseGrantedScopes(value: unknown): string[] | undefined {
    if (typeof value === 'string') {
        const parsed = value.split(/\s+/).filter(Boolean)
        return parsed.length > 0 ? parsed : undefined
    }
    if (Array.isArray(value) && value.length > 0 && value.every(v => typeof v === 'string')) {
        return value
    }
    return undefined
}

/**
 * A cached app entry is reusable as-is when it was registered with the
 * current full scope set, or when it was registered with the fallback
 * (no-push) scope set within PUSH_REJECTION_TTL_MS of the rejection that
 * caused the fallback. Anything else (no stamp, mismatched scopes, or an
 * expired fallback) needs re-registration.
 */
function isCacheHit(entry: { scopes?: string; pushRejectedAt?: number }): boolean {
    const scopes = entry.scopes ?? LEGACY_SCOPES
    if (sameScopeSet(scopes, SCOPES)) return true
    if (sameScopeSet(scopes, FALLBACK_SCOPES) && typeof entry.pushRejectedAt === 'number') {
        const { pushRejectedAt } = entry
        const now = Date.now()
        if (!Number.isFinite(pushRejectedAt) || pushRejectedAt > now) return false
        return now - pushRejectedAt < PUSH_REJECTION_TTL_MS
    }
    return false
}

export interface MastodonSession {
    access_token: string
    token_type: string
    scope: string
    created_at: number
    expires_in?: number
    refresh_token?: string
    account: {
        id: string
        username: string
        acct: string
        display_name: string
        url: string
        avatar: string
        note: string
    }
    instance_url: string
}

/**
 * Shared session type for all ActivityPub-based platforms (Mastodon, Pixelfed).
 * PixelfedSession is structurally identical to MastodonSession, so both can be
 * used interchangeably via this alias.
 */
export type ActivityPubSession = MastodonSession

// PKCE Helpers
function generateCodeVerifier() {
    const array = new Uint8Array(32)
    window.crypto.getRandomValues(array)
    return btoa(String.fromCharCode(...array))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}

async function generateCodeChallenge(verifier: string) {
    const encoder = new TextEncoder()
    const data = encoder.encode(verifier)
    const hash = await window.crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hash))
    return btoa(String.fromCharCode(...hashArray))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
}

/**
 * Known limitation (refresh-token-issuing servers only, e.g. Pleroma/Akkoma):
 * the cache is overwritten with the new client_id before the user completes
 * (or abandons) the OAuth consent screen. If they abandon it, an existing
 * session on that instance whose refresh_token belongs to the superseded
 * client_id will fail with invalid_grant on its next refresh. Mastodon
 * proper issues no refresh tokens and is unaffected. Not fixed here — see
 * mackinac#2900; fixing it would mean storing client_id per session rather
 * than per instance.
 */
export async function registerApp(instanceUrl: string): Promise<MastodonApp> {
    // Clean URL
    const url = instanceUrl.replace(/\/$/, '').replace(/^https?:\/\//, '')
    const fullUrl = `https://${url}`

    // Check if we already have an app for this instance
    const storedApps = JSON.parse(localStorage.getItem('mackinac_mastodon_apps') ?? '{}')
    const cachedEntry = storedApps[url]
    if (cachedEntry && isCacheHit(cachedEntry)) {
        // Strip any legacy client_secret from old stored entries
        const app = { ...cachedEntry }
        delete app.client_secret
        return { ...app, instance_url: fullUrl }
    }

    // A usable (if stale) cached app that sign-in can fall back to if
    // re-registration fails for a reason unrelated to scope support (429,
    // 5xx, network/timeout, or a non-fallback-eligible 4xx) — so a transient
    // registration failure doesn't lock out a user who works today. The
    // returned scopes match whatever the entry was actually registered with
    // (a legacy entry carries no stamp, hence the push-less default), so the
    // authorize/token-exchange calls that follow stay consistent with what
    // the server actually granted. Neither the cache nor any marker is
    // touched here, so the next sign-in retries the full registration.
    const degradedCachedApp = (): MastodonApp | undefined => {
        if (!cachedEntry) return undefined
        const app = { ...cachedEntry }
        delete app.client_secret
        return { ...app, scopes: cachedEntry.scopes ?? LEGACY_SCOPES, instance_url: fullUrl }
    }

    const registerWithScopes = (scopes: string) => {
        const formData = new FormData()
        formData.append('client_name', APP_NAME)
        formData.append('redirect_uris', window.location.origin + '/mastodon/callback')
        formData.append('scopes', scopes)
        formData.append('website', APP_WEBSITE)
        return fetch(`${fullUrl}/api/v1/apps`, { method: 'POST', body: formData })
    }

    let res: Response
    try {
        res = await registerWithScopes(SCOPES)
    } catch (err) {
        const degraded = degradedCachedApp()
        if (degraded) return degraded
        throw err
    }

    let registeredScopes = SCOPES
    let pushRejectedAt: number | undefined

    if (!res.ok) {
        // Retry without `push` only when the server rejects the scope value
        // itself (400/422). Any other failure — 401/403/404/other 4xx, 429,
        // 5xx — is not evidence that `push` is unsupported; rethrow (falling
        // back to a cached app if one exists) rather than masking it with a
        // fallback registration and a false push-rejection marker.
        if (res.status === 400 || res.status === 422) {
            let fallbackRes: Response
            try {
                fallbackRes = await registerWithScopes(FALLBACK_SCOPES)
            } catch (err) {
                const degraded = degradedCachedApp()
                if (degraded) return degraded
                throw err
            }
            if (!fallbackRes.ok) {
                const degraded = degradedCachedApp()
                if (degraded) return degraded
                throw new Error(`Failed to register app on ${url}`)
            }
            res = fallbackRes
            registeredScopes = FALLBACK_SCOPES
            pushRejectedAt = Date.now()
        } else {
            const degraded = degradedCachedApp()
            if (degraded) return degraded
            throw new Error(`Failed to register app on ${url}: HTTP ${res.status}`)
        }
    }

    let data: Awaited<ReturnType<typeof res.json>>
    try {
        data = await res.json()
    } catch {
        const degraded = degradedCachedApp()
        if (degraded) return degraded
        throw new Error(`Failed to register app on ${url}`)
    }

    if (typeof data?.client_id !== 'string') {
        const degraded = degradedCachedApp()
        if (degraded) return degraded
        throw new Error(`Failed to register app on ${url}`)
    }

    // A server can return 2xx while silently dropping a scope it doesn't
    // support (Pleroma/Akkoma style). If it reports which scopes it actually
    // granted, and `push` isn't among them despite being requested, stamp the
    // fixed fallback scope set — not whatever subset of scopes the server
    // happened to list — and mark the rejection. Stamping the literal granted
    // list (including a server that only lists granular scope names the
    // requested-scope filter doesn't recognize) would produce a scope string
    // that never matches FALLBACK_SCOPES in isCacheHit, causing the entry to
    // miss the cache and re-register on every call.
    if (registeredScopes === SCOPES) {
        const granted = parseGrantedScopes(data.scopes)
        if (granted && !granted.includes('push')) {
            registeredScopes = FALLBACK_SCOPES
            pushRejectedAt = Date.now()
        }
    }

    // client_secret is returned in memory so login() can stash it in sessionStorage for the
    // token exchange. It is never written to localStorage.
    const app: MastodonApp = {
        client_id: data.client_id,
        client_secret: data.client_secret,
        vapid_key: data.vapid_key,
        instance_url: fullUrl,
        scopes: registeredScopes,
        ...(pushRejectedAt !== undefined ? { pushRejectedAt } : {}),
    }

    // Store app credentials without client_secret
    const appToStore = { ...app }
    delete appToStore.client_secret
    storedApps[url] = appToStore
    localStorage.setItem('mackinac_mastodon_apps', JSON.stringify(storedApps))

    return app
}

/**
 * True when the cached app for `host` was registered without `push` because
 * the server rejected it, and that rejection is still within
 * PUSH_REJECTION_TTL_MS. A future "enable push notifications" UI can use
 * this to avoid prompting on instances that don't support it.
 */
export function isPushUnsupported(host: string): boolean {
    const url = host.replace(/\/$/, '').replace(/^https?:\/\//, '')
    try {
        const storedApps = JSON.parse(localStorage.getItem('mackinac_mastodon_apps') ?? '{}')
        const entry = storedApps[url]
        if (!entry || typeof entry.pushRejectedAt !== 'number') return false
        const { pushRejectedAt } = entry
        const now = Date.now()
        if (!Number.isFinite(pushRejectedAt) || pushRejectedAt > now) return false
        return now - pushRejectedAt < PUSH_REJECTION_TTL_MS
    } catch {
        return false
    }
}

export async function login(instanceUrl: string) {
    if (!window.crypto?.subtle) {
        throw new Error(
            'WebCrypto is not available. Your browser requires a trusted HTTPS certificate for OAuth to work.'
        )
    }

    try {
        const app = await registerApp(instanceUrl)
        const verifier = await generateCodeVerifier()
        const challenge = await generateCodeChallenge(verifier)

        // Generate random state for CSRF protection
        const state = await generateCodeVerifier() // Reusing verifier generator for random string

        // Store verifier, instance, and state for callback
        sessionStorage.setItem('mastodon_verifier', verifier)
        sessionStorage.setItem('mastodon_instance', app.instance_url)
        sessionStorage.setItem('mastodon_state', state)
        // Stash secret for token exchange; sessionStorage is tab-scoped and cleared on tab
        // close, making it far less exposed than localStorage (the original finding).
        // Always clear any secret left behind by an abandoned prior flow (possibly for a
        // different instance) before conditionally setting this one's, so a stale secret
        // can never survive into this attempt's token exchange (mackinac#2898).
        sessionStorage.removeItem('mastodon_client_secret')
        if (app.client_secret) {
            sessionStorage.setItem('mastodon_client_secret', app.client_secret)
        }

        const redirectUri = window.location.origin + '/mastodon/callback'
        const params = new URLSearchParams({
            client_id: app.client_id,
            redirect_uri: redirectUri,
            response_type: 'code',
            scope: app.scopes ?? LEGACY_SCOPES,
            code_challenge: challenge,
            code_challenge_method: 'S256',
            state: state,
        })

        const authorizeUrl = `${app.instance_url}/oauth/authorize?${params.toString()}`
        console.log('Mastodon OAuth redirect:', authorizeUrl)
        window.location.href = authorizeUrl
    } catch (err) {
        console.error('Mastodon login failed:', err)
        throw err
    }
}

export async function handleCallback(code: string): Promise<MastodonSession> {
    const verifier = sessionStorage.getItem('mastodon_verifier')
    const instanceUrl = sessionStorage.getItem('mastodon_instance')
    const storedState = sessionStorage.getItem('mastodon_state')
    const clientSecret = sessionStorage.getItem('mastodon_client_secret')

    // The secret is only ever needed for this one exchange — clear it on any exit (success
    // or failure, including the early validation throws below) so a failed or aborted
    // callback can never leave it behind for the next attempt (mackinac#2898, mackinac#2899).
    try {
        // Get state from URL params
        const params = new URLSearchParams(window.location.search)
        const returnedState = params.get('state')

        if (!verifier || !instanceUrl) {
            throw new Error('Missing PKCE data')
        }

        if (!storedState || !returnedState || storedState !== returnedState) {
            throw new Error('Invalid state parameter (potential CSRF attack)')
        }

        // Clean URL to get key for stored app
        const urlKey = instanceUrl.replace(/\/$/, '').replace(/^https?:\/\//, '')
        const storedApps = JSON.parse(localStorage.getItem('mackinac_mastodon_apps') ?? '{}')
        const app = storedApps[urlKey]

        if (!app) {
            throw new Error('App registration not found')
        }

        if (typeof app.client_id !== 'string') {
            throw new Error('Invalid app registration: client_id is missing or not a string')
        }

        const formData = new FormData()
        formData.append('client_id', app.client_id)
        // Include client_secret if available (improves compatibility with Mastodon < 4.3,
        // Pleroma, Akkoma, GoToSocial). Falls back to PKCE-only for servers that issued none.
        if (clientSecret) {
            formData.append('client_secret', clientSecret)
        }
        formData.append('redirect_uri', window.location.origin + '/mastodon/callback')
        formData.append('grant_type', 'authorization_code')
        formData.append('code', code)
        formData.append('code_verifier', verifier)
        formData.append('scope', app.scopes ?? LEGACY_SCOPES)

        const res = await fetch(`${instanceUrl}/oauth/token`, {
            method: 'POST',
            body: formData,
        })

        if (!res.ok) {
            throw new Error('Failed to exchange token')
        }

        const tokenData = await res.json()

        // Verify credentials to get user info
        const verifyRes = await fetch(`${instanceUrl}/api/v1/accounts/verify_credentials`, {
            headers: {
                Authorization: `Bearer ${tokenData.access_token}`,
            },
        })

        if (!verifyRes.ok) {
            throw new Error('Failed to verify credentials')
        }

        const account = await verifyRes.json()

        const session: MastodonSession = {
            ...tokenData,
            refresh_token: tokenData.refresh_token,
            expires_in: tokenData.expires_in,
            account,
            instance_url: instanceUrl,
        }

        // Clear remaining PKCE data immediately after use
        sessionStorage.removeItem('mastodon_verifier')
        sessionStorage.removeItem('mastodon_instance')
        sessionStorage.removeItem('mastodon_state')

        return session
    } finally {
        sessionStorage.removeItem('mastodon_client_secret')
    }
}

export async function refreshToken(session: MastodonSession): Promise<MastodonSession> {
    if (!session.refresh_token) {
        throw new Error('No refresh token available')
    }

    // Clean URL to get key for stored app
    const urlKey = session.instance_url.replace(/\/$/, '').replace(/^https?:\/\//, '')
    const storedApps = JSON.parse(localStorage.getItem('mackinac_mastodon_apps') ?? '{}')
    const app = storedApps[urlKey]

    if (!app) {
        throw new Error('App registration not found')
    }

    const formData = new FormData()
    formData.append('client_id', app.client_id)
    // client_secret is not sent at refresh time: it was held in sessionStorage only for
    // the initial exchange and deleted immediately after. Refresh without a secret is
    // accepted by Mastodon 4.3+; older servers may not issue refresh tokens at all.
    formData.append('grant_type', 'refresh_token')
    formData.append('refresh_token', session.refresh_token)

    const res = await fetch(`${session.instance_url}/oauth/token`, {
        method: 'POST',
        body: formData,
    })

    if (!res.ok) {
        throw new Error('Failed to refresh token')
    }

    const tokenData = await res.json()

    // Update session with new tokens
    const newSession: MastodonSession = {
        ...session,
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token ?? session.refresh_token, // Use new if provided, else keep old
        expires_in: tokenData.expires_in,
        created_at: Math.floor(Date.now() / 1000), // Update creation time
    }

    addStoredSession(newSession)
    return newSession
}

export async function ensureValidSession(session: MastodonSession): Promise<MastodonSession> {
    // Check if token is expired or about to expire (e.g., within 5 minutes)
    // created_at is in seconds (Unix timestamp)
    // expires_in is in seconds
    if (session.expires_in) {
        const now = Math.floor(Date.now() / 1000)
        const expiresAt = session.created_at + session.expires_in
        const buffer = 300 // 5 minutes buffer

        if (now >= expiresAt - buffer) {
            console.log('Mastodon token expired or expiring soon, refreshing...')
            return refreshToken(session)
        }
    }
    return session
}

export function hasPushScope(session: { scope?: string | null } | null | undefined): boolean {
    return typeof session?.scope === 'string' && session.scope.split(/\s+/).includes('push')
}

export function reauthorizeForPush(host: string) {
    return login(host)
}

const SESSIONS_KEY = 'mackinac_mastodon_sessions'
const OLD_SESSION_KEY = 'mackinac_mastodon_session'

export function getMastodonAccountId(session: MastodonSession): string {
    if (!session?.instance_url) return ''
    const instanceHost = session.instance_url.replace(/^https?:\/\//, '').replace(/\/$/, '')
    return `mastodon:${instanceHost}:${session.account.username}`
}

/**
 * Strip client_secret from any previously stored app registrations.
 * Ensures existing users are cleaned up when upgrading to public-client mode.
 */
function migrateStoredApps(): void {
    try {
        const stored = localStorage.getItem('mackinac_mastodon_apps')
        if (!stored) return
        const apps: Record<string, Record<string, unknown>> = JSON.parse(stored)
        let changed = false
        for (const key of Object.keys(apps)) {
            if ('client_secret' in apps[key]) {
                delete apps[key].client_secret
                changed = true
            }
        }
        if (changed) {
            localStorage.setItem('mackinac_mastodon_apps', JSON.stringify(apps))
        }
    } catch {
        // Corrupt data — ignore
    }
}

/**
 * Migrate from old single-session storage to new array storage.
 * Called once on first load — reads old key, wraps in array, writes new key, deletes old.
 */
function migrateSessionStorage(): void {
    try {
        const old = localStorage.getItem(OLD_SESSION_KEY)
        if (!old) return
        const existing = localStorage.getItem(SESSIONS_KEY)
        if (existing) {
            // New key already exists — just clean up old key
            localStorage.removeItem(OLD_SESSION_KEY)
            return
        }
        localStorage.setItem(SESSIONS_KEY, JSON.stringify([JSON.parse(old)]))
        localStorage.removeItem(OLD_SESSION_KEY)
    } catch {
        // Corrupt data — start fresh
    }
}

// Run migrations on module load
migrateStoredApps()
migrateSessionStorage()

export function getStoredSessions(): MastodonSession[] {
    try {
        const stored = localStorage.getItem(SESSIONS_KEY)
        return stored ? JSON.parse(stored) : []
    } catch {
        return []
    }
}

/** @deprecated Use getStoredSessions() for multi-account. Kept for backward compat during transition. */
export function getStoredSession(): MastodonSession | null {
    const sessions = getStoredSessions()
    return sessions[0] ?? null
}

export function addStoredSession(session: MastodonSession): void {
    const sessions = getStoredSessions()
    const id = getMastodonAccountId(session)
    // Replace existing session for the same account, or append
    const idx = sessions.findIndex(s => getMastodonAccountId(s) === id)
    if (idx >= 0) {
        sessions[idx] = session
    } else {
        sessions.push(session)
    }
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions))
}

export function removeStoredSession(accountId: string): void {
    const sessions = getStoredSessions().filter(s => getMastodonAccountId(s) !== accountId)
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions))
}

export function clearSession(accountId?: string) {
    if (accountId) {
        removeStoredSession(accountId)
    } else {
        localStorage.removeItem(SESSIONS_KEY)
    }
}

export async function lookupAccount(
    session: MastodonSession,
    handle: string
): Promise<{ id: string }> {
    const account = await lookupAccountOnInstance(session.instance_url, handle)
    return { id: account.id }
}

export async function getRelationship(
    session: MastodonSession,
    accountId: string
): Promise<{ id: string; following: boolean; followed_by: boolean; blocking: boolean }> {
    const hostname = new URL(session.instance_url).hostname
    if (!isSafeHostname(hostname)) throw new Error(`Unsafe hostname: ${hostname}`)

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(
            `${session.instance_url}/api/v1/accounts/relationships?id[]=${encodeURIComponent(accountId)}`,
            {
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                signal: controller.signal,
            }
        )

        if (!res.ok) {
            throw new Error('Failed to get relationship')
        }

        const data = await res.json()
        if (!Array.isArray(data) || data.length === 0) {
            throw new Error(`No relationship data returned for account ${accountId}`)
        }
        return data[0]
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Check whether the given ActivityPub account follows the authenticated user.
 * Looks up the account on the session's instance, then fetches the relationship.
 * Returns false (not true) if the lookup or relationship fetch fails.
 */
export async function getAPFollowedBy(session: MastodonSession, handle: string): Promise<boolean> {
    const account = await lookupAccountOnInstance(session.instance_url, handle)
    const relationship = await getRelationship(session, account.id)
    return relationship.followed_by
}

/**
 * Fetch follow relationships for multiple accounts in a single API call.
 * Returns a map of accountId -> { following }.
 */
export async function getBatchRelationships(
    session: { instance_url: string; access_token: string },
    accountIds: string[]
): Promise<Map<string, { following: boolean }>> {
    if (accountIds.length === 0) return new Map()
    const hostname = new URL(session.instance_url).hostname
    if (!isSafeHostname(hostname)) throw new Error(`Unsafe hostname: ${hostname}`)
    const params = accountIds.map(id => `id[]=${encodeURIComponent(id)}`).join('&')
    const res = await fetch(`${session.instance_url}/api/v1/accounts/relationships?${params}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
    })
    if (!res.ok) throw new Error('Failed to get batch relationships')
    const data: unknown = await res.json()
    if (!Array.isArray(data)) throw new Error('Unexpected response format')
    return new Map(
        (data as Array<{ id: string; following: boolean }>).map(r => [
            r.id,
            { following: r.following },
        ])
    )
}

export async function followAccount(session: MastodonSession, accountId: string): Promise<void> {
    const res = await fetch(
        `${session.instance_url}/api/v1/accounts/${encodeURIComponent(accountId)}/follow`,
        {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
        }
    )

    if (!res.ok) {
        throw new Error('Failed to follow account')
    }
}

export async function unfollowAccount(session: MastodonSession, accountId: string): Promise<void> {
    const res = await fetch(
        `${session.instance_url}/api/v1/accounts/${encodeURIComponent(accountId)}/unfollow`,
        {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
        }
    )

    if (!res.ok) {
        throw new Error('Failed to unfollow account')
    }
}

export async function updateProfile(
    session: MastodonSession,
    data: {
        display_name?: string
        note?: string
        fields_attributes?: { name: string; value: string }[]
    }
): Promise<MastodonSession['account']> {
    const formData = new FormData()
    if (data.display_name !== undefined) formData.append('display_name', data.display_name)
    if (data.note !== undefined) formData.append('note', data.note)
    if (data.fields_attributes !== undefined) {
        data.fields_attributes.forEach((field, i) => {
            formData.append(`fields_attributes[${i}][name]`, field.name)
            formData.append(`fields_attributes[${i}][value]`, field.value)
        })
    }

    const res = await fetch(`${session.instance_url}/api/v1/accounts/update_credentials`, {
        method: 'PATCH',
        headers: {
            Authorization: `Bearer ${session.access_token}`,
        },
        body: formData,
    })

    if (!res.ok) {
        throw new Error('Failed to update Mastodon profile')
    }

    return res.json()
}

// --- Unauthenticated instance API (for public social graph) ---

export interface MastodonAccount {
    id: string
    username: string
    acct: string
    display_name: string
    avatar: string
    note: string
    url: string
    uri: string
    followers_count: number
    following_count: number
    statuses_count: number
    fields?: Array<{ name: string; value: string; verified_at?: string | null }>
}

export interface MastodonAccountPage {
    accounts: MastodonAccount[]
    nextUrl?: string
}

/**
 * Parse the instance domain from an AP handle (user@domain → https://domain)
 */
export function instanceUrlFromHandle(handle: string): string {
    const atIndex = handle.indexOf('@')
    if (atIndex === -1) throw new Error(`Invalid AP handle: ${handle}`)
    const domain = handle.slice(atIndex + 1)
    return `https://${domain}`
}

// --- Account lookup cache ---

const ACCOUNT_LOOKUP_CACHE_TTL_MS = 10 * 60 * 1000 // 10 minutes
const ACCOUNT_LOOKUP_CACHE_MAX = 200

interface CachedAccountLookup {
    account: MastodonAccount
    expiresAt: number
}

const accountLookupCache = new Map<string, CachedAccountLookup>()

// In-flight lookups keyed the same as accountLookupCache, so concurrent
// callers (e.g. the profile header and the posts feed both resolving the
// signed-in user's own account on mount) await one shared request instead of
// each firing their own before the first has a chance to populate the cache.
const pendingAccountLookups = new Map<string, Promise<MastodonAccount>>()

/**
 * Drop every cached account lookup so the next lookupAccountOnInstance call for any
 * account hits the network. Called by useProfile.saveMastodonFields after a successful
 * fields update, because the signed-in user's own profile is read through this cache and
 * would otherwise replay the pre-save fields (including rel="me" verification) for up to
 * ACCOUNT_LOOKUP_CACHE_TTL_MS; a full clear is used rather than a per-account eviction
 * because the own-profile key is derived from the handle in profileService/
 * fetchAPProfileStats, not from the session's instance_url (#2678). Also used by tests.
 * Does not cancel an in-flight lookup, which will still populate the cache when it
 * resolves.
 */
export function clearAccountLookupCache(): void {
    accountLookupCache.clear()
}

/**
 * Lookup an account on a Mastodon instance without authentication.
 * Validates hostname before any network I/O to prevent SSRF.
 * Results are cached for 10 minutes with LRU eviction at 200 entries.
 * Concurrent lookups for the same instance+username share one in-flight request.
 */
export async function lookupAccountOnInstance(
    instanceUrl: string,
    username: string
): Promise<MastodonAccount> {
    const { origin: expectedOrigin } = canonicalizeInstanceOrigin(instanceUrl)

    const cacheKey = `${instanceUrl}:${username}`
    const cached = accountLookupCache.get(cacheKey)
    if (cached && Date.now() < cached.expiresAt) {
        return cached.account
    }

    const pending = pendingAccountLookups.get(cacheKey)
    if (pending) {
        return pending
    }

    const lookupPromise = (async () => {
        const url = new URL('/api/v1/accounts/lookup', expectedOrigin)
        url.searchParams.set('acct', username)

        const result = await mastodonFetchJsonWithStatus<MastodonAccount>(
            url.toString(),
            {},
            { expectedOrigin }
        )

        if (!result.ok) {
            throw new Error(`Failed to lookup account: ${username} on ${instanceUrl}`)
        }

        const account = result.data

        if (accountLookupCache.size >= ACCOUNT_LOOKUP_CACHE_MAX) {
            const firstKey = accountLookupCache.keys().next().value
            if (firstKey !== undefined) accountLookupCache.delete(firstKey)
        }
        accountLookupCache.set(cacheKey, {
            account,
            expiresAt: Date.now() + ACCOUNT_LOOKUP_CACHE_TTL_MS,
        })

        return account
    })()

    pendingAccountLookups.set(cacheKey, lookupPromise)
    try {
        return await lookupPromise
    } finally {
        pendingAccountLookups.delete(cacheKey)
    }
}

/**
 * Extract the first href URL from an HTML string (e.g. Mastodon field values).
 */
export function extractUrlFromHtml(html: string): string | undefined {
    const match = html.match(/href="([^"]+)"/)
    return match?.[1]
}

/**
 * Match a field name to a known platform icon identifier.
 * Returns the platform id if matched, otherwise 'link'.
 */
function matchPlatformIcon(name: string): string {
    const lower = name.toLowerCase()
    for (const platform of PLATFORMS) {
        // Use word boundary for short IDs (e.g. 'x') to avoid false positives
        if (platform.id.length <= 2) {
            const wordBoundary = new RegExp(`\\b${platform.id}\\b`)
            if (wordBoundary.test(lower)) return platform.id
        } else if (lower.includes(platform.id)) {
            return platform.id
        }
        if (lower.includes(platform.name.toLowerCase())) {
            return platform.id
        }
    }
    return 'link'
}

/**
 * Fetch profile stats for an AP handle directly from the Mastodon instance.
 * Also extracts profile fields as LinkedAccount[] format.
 *
 * `verified_at` is carried through to `LinkedAccount.verifiedAt` for the verified-link
 * badge; the key is omitted (not null) when unverified, absent, empty or whitespace-only,
 * so key presence and truthiness are the same test downstream; the value is never parsed.
 */
export async function fetchAPProfileStats(handle: string): Promise<{
    followersCount: number
    followsCount: number
    postsCount: number
    linkedAccounts?: LinkedAccount[]
    rawFields?: { name: string; value: string }[]
}> {
    const instanceUrl = instanceUrlFromHandle(handle)
    const atIndex = handle.indexOf('@')
    const username = handle.slice(0, atIndex)
    const account = await lookupAccountOnInstance(instanceUrl, username)

    let linkedAccounts: LinkedAccount[] | undefined
    let rawFields: { name: string; value: string }[] | undefined
    if (account.fields && account.fields.length > 0) {
        const mapped: LinkedAccount[] = []
        for (let i = 0; i < account.fields.length; i++) {
            const field = account.fields[i]
            const url = extractUrlFromHtml(field.value)
            if (!url || !isSafeUrl(url)) continue
            mapped.push({
                id: `field-${i}`,
                rkey: `field-${i}`,
                name: field.name,
                link: url,
                icon: matchPlatformIcon(field.name),
                order: i,
                ...(typeof field.verified_at === 'string' && field.verified_at.trim().length > 0
                    ? { verifiedAt: field.verified_at }
                    : {}),
            })
        }
        if (mapped.length > 0) linkedAccounts = mapped

        rawFields = account.fields.map(f => ({
            name: f.name,
            value: extractUrlFromHtml(f.value) ?? stripHTML(f.value),
        }))
    }

    return {
        followersCount: account.followers_count,
        followsCount: account.following_count,
        postsCount: account.statuses_count,
        linkedAccounts,
        rawFields,
    }
}

/**
 * Parse a Mastodon Link header to extract the "next" page URL.
 * Mastodon returns: Link: <url>; rel="next", <url>; rel="prev"
 */
export function parseLinkHeader(header: string | null): string | undefined {
    if (!header) return undefined
    const parts = header.split(',')
    for (const part of parts) {
        const match = part.match(/<([^>]+)>;\s*rel="next"/)
        if (match) return match[1]
    }
    return undefined
}

/**
 * Strip HTML tags and decode common entities from Mastodon note fields.
 */
export function stripHTML(html: string): string {
    let result = html.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>\s*<p>/gi, '\n\n')
    result = result.replace(/<[^>]+>/g, '')
    result = result
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
    return result.trim()
}

/**
 * Normalize a Mastodon `acct` field to full user@domain format.
 * Same-instance accounts return just the username, so we append the instance domain.
 *
 * instanceUrl is the instance the account JSON was fetched from; only its host is
 * used — path, query, fragment, credentials, and letter case are discarded.
 */
export function normalizeAcct(acct: string, instanceUrl: string): string {
    if (acct.includes('@')) return acct
    let domain: string
    try {
        domain = new URL(instanceUrl).host.toLowerCase()
    } catch {
        domain = instanceUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')
    }
    return `${acct}@${domain}`
}

/**
 * Normalize a Mastodon instance base URL for use as a React Query key segment.
 * Lowercases the hostname, drops default ports (handled by the URL parser),
 * and strips any trailing slash while retaining a non-root base path.
 */
export function normalizeInstanceUrl(instanceUrl: string): string {
    const parsed = new URL(instanceUrl)
    const path = parsed.pathname.replace(/\/$/, '')
    return `${parsed.protocol}//${parsed.host.toLowerCase()}${path}`
}

/**
 * Validate a pagination cursor extracted from a Link header against the
 * expected origin. Returns the cursor if safe, or `undefined` (dropping the
 * cursor) if it fails validation — logging a sanitized warning (origins
 * only, never the full URL/query string/headers) rather than throwing, since
 * a hostile Link header should end pagination, not break the current page.
 */
function resolveNextUrl(
    candidateNextUrl: string | undefined,
    expectedOrigin: string,
    operation: string
): string | undefined {
    if (!candidateNextUrl) return undefined

    try {
        assertSafeMastodonUrl(candidateNextUrl, expectedOrigin)
        return candidateNextUrl
    } catch {
        let receivedOrigin = '[unparseable]'
        try {
            receivedOrigin = new URL(candidateNextUrl).origin
        } catch {
            // leave sentinel
        }
        console.warn(
            `[${operation}] dropping unsafe pagination Link cursor: expected origin ${expectedOrigin}, received origin ${receivedOrigin}`
        )
        return undefined
    }
}

/**
 * Fetch followers for an account on a Mastodon instance.
 * Pass `accessToken` for instances (e.g. Pixelfed) that require authentication.
 *
 * The first-page URL and any server-supplied pagination cursor are both
 * pinned to `instanceUrl`'s own origin before any network I/O — see
 * `webapp/src/lib/mastodonFetch.ts` for the threat model.
 */
export async function fetchInstanceFollowers(
    instanceUrl: string,
    accountId: string,
    options?: { nextUrl?: string; limit?: number; accessToken?: string }
): Promise<MastodonAccountPage> {
    const { origin: expectedOrigin } = canonicalizeInstanceOrigin(instanceUrl)
    const url =
        options?.nextUrl ??
        `${expectedOrigin}/api/v1/accounts/${encodeURIComponent(accountId)}/followers?limit=${options?.limit ?? 40}`

    const headers: Record<string, string> = {}
    if (options?.accessToken) headers['Authorization'] = `Bearer ${options.accessToken}`

    const result = await mastodonFetchJsonWithStatus<MastodonAccount[]>(
        url,
        { headers },
        { expectedOrigin }
    )
    if (!result.ok) {
        throw new Error(`Failed to fetch followers: ${result.status}`)
    }

    const accounts = result.data
    const nextUrl = resolveNextUrl(
        parseLinkHeader(result.linkHeader),
        expectedOrigin,
        'fetchInstanceFollowers'
    )

    return { accounts, nextUrl }
}

/**
 * Fetch following for an account on a Mastodon instance.
 * Pass `accessToken` for instances (e.g. Pixelfed) that require authentication.
 *
 * The first-page URL and any server-supplied pagination cursor are both
 * pinned to `instanceUrl`'s own origin before any network I/O — see
 * `webapp/src/lib/mastodonFetch.ts` for the threat model.
 */
export async function fetchInstanceFollowing(
    instanceUrl: string,
    accountId: string,
    options?: { nextUrl?: string; limit?: number; accessToken?: string }
): Promise<MastodonAccountPage> {
    const { origin: expectedOrigin } = canonicalizeInstanceOrigin(instanceUrl)
    const url =
        options?.nextUrl ??
        `${expectedOrigin}/api/v1/accounts/${encodeURIComponent(accountId)}/following?limit=${options?.limit ?? 40}`

    const headers: Record<string, string> = {}
    if (options?.accessToken) headers['Authorization'] = `Bearer ${options.accessToken}`

    const result = await mastodonFetchJsonWithStatus<MastodonAccount[]>(
        url,
        { headers },
        { expectedOrigin }
    )
    if (!result.ok) {
        throw new Error(`Failed to fetch following: ${result.status}`)
    }

    const accounts = result.data
    const nextUrl = resolveNextUrl(
        parseLinkHeader(result.linkHeader),
        expectedOrigin,
        'fetchInstanceFollowing'
    )

    return { accounts, nextUrl }
}
